import { useMemo } from "react";
import { Crown, Clock, Coins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const DAYS = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];
const BLOCKS = ["12–4am", "4–8am", "8am–12pm", "12–4pm", "4–8pm", "8pm–12am"];
const tooltipStyle = { background: "#1A1A1A", border: "1px solid #2A2A2A", borderRadius: 8, color: "#fff", fontSize: 12 };

export const TopFans = ({ fans, renderActions }) => (
  <div className="pe-card rounded-2xl p-5" data-testid="top-fans">
    <div className="text-sm text-white font-medium mb-4 flex items-center gap-2"><Crown size={15} className="text-[#D4AF37]" /> Top fans</div>
    {fans.length === 0 ? (
      <div className="h-40 flex items-center justify-center text-xs text-[#A0A0A0]">Your biggest supporters will appear here</div>
    ) : (
      <div className="space-y-3">
        {fans.map((f, i) => (
          <div key={f.id} data-testid={`top-fan-${i}`} className="flex items-center gap-3">
            <span className="w-6 text-center text-sm font-semibold text-[#D4AF37]">{i + 1}</span>
            <div className="w-9 h-9 rounded-full overflow-hidden bg-[#1A1A1A] border border-[#D4AF37]/30 flex-shrink-0 flex items-center justify-center text-sm text-[#D4AF37] font-semibold">
              {f.photo ? <img src={f.photo} alt="" className="w-full h-full object-cover" /> : (f.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{f.name}</div>
              {f.subscriber && <span className="text-[10px] text-[#D4AF37]">Subscriber</span>}
            </div>
            <div className="text-right text-xs text-[#A0A0A0]">
              {f.coins > 0 && <div className="flex items-center justify-end gap-1"><Coins size={11} className="text-[#D4AF37]" /> {f.coins}</div>}
              {f.cash > 0 && <div>${f.cash.toFixed(2)}</div>}
            </div>
            {renderActions?.(f)}
          </div>
        ))}
      </div>
    )}
  </div>
);

const slotStats = (activity) => {
  const blocks = BLOCKS.map((label) => ({ label, views: 0, posts: 0 }));
  const days = DAYS.map(() => ({ views: 0, posts: 0 }));
  activity.forEach((p) => {
    const d = new Date(p.created_at);
    if (isNaN(d)) return;
    const b = blocks[Math.floor(d.getHours() / 4)]; b.views += p.views; b.posts += 1;
    const dd = days[d.getDay()]; dd.views += p.views; dd.posts += 1;
  });
  const avg = (x) => (x.posts ? x.views / x.posts : 0);
  const bi = blocks.reduce((best, b, i) => (avg(b) > avg(blocks[best]) ? i : best), 0);
  const di = days.reduce((best, d, i) => (avg(d) > avg(days[best]) ? i : best), 0);
  return { blocks, avg, bi, di, hasData: activity.some((p) => p.views > 0) };
};

// Next upcoming moment in the creator's best day + time block (defaults to Friday 9pm).
export const nextBestTime = (activity) => {
  const { bi, di, hasData } = slotStats(activity);
  const day = hasData ? di : 5;
  const hour = hasData ? bi * 4 + 1 : 21;
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  d.setDate(d.getDate() + ((day - d.getDay() + 7) % 7));
  if (d.getTime() < Date.now() + 5 * 60 * 1000) d.setDate(d.getDate() + 7);
  return d;
};

export const BestTimes = ({ activity }) => {
  const { chart, bestDay, bestBlock } = useMemo(() => {
    const { blocks, avg, bi, di } = slotStats(activity);
    return { chart: blocks.map((b) => ({ label: b.label, avg: Math.round(avg(b)) })), bestDay: DAYS[di], bestBlock: BLOCKS[bi] };
  }, [activity]);
  const hasData = activity.some((p) => p.views > 0);
  return (
    <div className="pe-card rounded-2xl p-5" data-testid="best-times">
      <div className="text-sm text-white font-medium mb-1 flex items-center gap-2"><Clock size={15} className="text-[#D4AF37]" /> Best time to post</div>
      {!hasData ? (
        <div className="h-40 flex items-center justify-center text-xs text-[#A0A0A0]">Post a few times to discover when fans are watching</div>
      ) : (
        <>
          <div className="text-xs text-[#A0A0A0] mb-3" data-testid="best-times-tip">Your posts do best on <span className="text-[#D4AF37]">{bestDay}</span>, <span className="text-[#D4AF37]">{bestBlock}</span> (your local time).</div>
          <div style={{ width: "100%", height: 170 }}>
            <ResponsiveContainer>
              <BarChart data={chart} margin={{ left: -20 }}>
                <CartesianGrid stroke="#222" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#777", fontSize: 9 }} />
                <YAxis tick={{ fill: "#777", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(212,175,55,0.08)" }} formatter={(v) => [`${v} avg views`, ""]} />
                <Bar dataKey="avg" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};
